<script>
import { debounce } from '@utils/performance'

/**
 * BaseSearchInput - Reusable search input component with debouncing
 *
 * Emits:
 * - @input: v-model binding for keyword value
 * - @search: Debounced search event fired after user stops typing
 * - @clear: Fired when clear button is clicked
 *
 * Slots:
 * - prepend: Content before the input (default: search icon)
 * - append: Content after the input (default: clear icon when hasInputValue is true)
 *   - Slot props: { clear: Function, hasInputValue: Boolean }
 */
export default {
    props: {
        value: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Search',
        },
        debounceDelay: {
            type: Number,
            default: 500,
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
        inputClass: {
            type: [String, Array, Object],
            default: '',
        },
        searchIconClass: {
            type: [String, Array, Object],
            default: '',
        },
        clearIconClass: {
            type: [String, Array, Object],
            default: '',
        },
    },
    computed: {
        localValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
    },
    created() {
        this.debouncedSearch = debounce(() => {
            this.$emit('search', this.localValue)
        }, this.debounceDelay)
    },
    mounted() {
        if (this.autofocus) {
            this.focus()
        }
    },
    methods: {
        onKeyup() {
            this.debouncedSearch()
        },
        clear() {
            this.localValue = ''
            this.$emit('clear')
        },
        focus() {
            this.$refs.input && this.$refs.input.focus()
        },
    },
}
</script>

<template>
    <div class="wrapper">
        <slot name="prepend">
            <BaseIcon
                :class="searchIconClass"
                name="search"
            />
        </slot>
        <input
            ref="input"
            v-model="localValue"
            :class="inputClass"
            type="text"
            inputmode="search"
            name="search-query"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            role="searchbox"
            data-lpignore="true"
            data-form-type="other"
            :placeholder="placeholder"
            @keyup.passive="onKeyup"
        >
        <slot
            name="append"
            :clear="clear"
            :hasInputValue="!!localValue"
        >
            <BaseIcon
                v-if="localValue"
                :class="clearIconClass"
                name="x"
                @click.native="clear"
            />
        </slot>
    </div>
</template>

<style lang="scss" scoped>
.wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
}
</style>
